<template>
    <div
        class="nk-sidebar nk-sidebar-fixed nk-sidebar-resizable sip-school-sidebar"
        :class="{ 'is-user-collapsed': isUserCollapsed }"
        id="MainSideBarRightMenu"
        data-content="sidebarMenu"
        :style="sidebarStyle"
    >
        <div class="nk-sidebar-element nk-sidebar-head">
            <div class="nk-sidebar-brand d-flex align-items-center justify-content-between w-100">
                <a href="/home" class="sip-school-brand" aria-label="Home">
                    <span class="sip-school-brand__mark">
                        <img
                            v-if="!logoLoadError && getLogoUrl"
                            class="sip-school-brand__logo"
                            :class="{ 'is-fallback': isDarkMode && !hasDarkLogo }"
                            :src="getLogoUrl"
                            alt="Sipinor"
                            @error="logoLoadError = true"
                        >
                        <admin-icon v-else name="school" :size="isUserCollapsed ? 30 : 38"></admin-icon>
                    </span>
                </a>
            </div>
            <div class="nk-menu-trigger me-n2">
                <a class="nk-nav-toggle nk-quick-nav-icon d-xl-none" data-target="sidebarMenu" id="HideRightSideBarMenu" aria-label="Close menu">
                    <admin-icon name="close" :size="18"></admin-icon>
                </a>
            </div>
        </div>

        <div class="nk-sidebar-element">
            <div class="nk-sidebar-content">
                <div class="nk-sidebar-menu">
                    <main-menu></main-menu>
                </div>
            </div>
        </div>

        <div class="nk-sidebar-element sip-side-profile-wrap">
            <sidebar-profile :is-collapsed="isUserCollapsed"></sidebar-profile>
        </div>

        <div
            class="nk-sidebar-resize-handle"
            title="Drag to resize sidebar"
            @mousedown.prevent="startResize"
        ></div>
    </div>
</template>

<script>
import mainMenu from './menu'
import SidebarProfile from './sidebar-profile'

const STORAGE_KEY = 'school_sidebar_width'
const MIN_WIDTH = 72
const MAX_WIDTH = 400
const COLLAPSED_THRESHOLD = 90
const COLLAPSED_WIDTH = 72
const DEFAULT_WIDTH = 260
const DESKTOP_BREAKPOINT = 992

export default {
    components: { mainMenu, SidebarProfile },
    props: {
        isDarkMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            logoLoadError: false,
            sidebarWidth: DEFAULT_WIDTH,
            isResizing: false,
            viewportWidth: typeof window !== 'undefined' ? window.innerWidth : DEFAULT_WIDTH,
        };
    },
    computed: {
        hasDarkLogo() {
            return !!(this.$store && this.$store.getters.getConfig('logo_dark'));
        },
        getLogoUrl() {
            if (this.isDarkMode && this.hasDarkLogo) {
                return helper.getDarkLogo();
            }
            return helper.getLogo();
        },
        isUserCollapsed() {
            return this.sidebarWidth <= COLLAPSED_THRESHOLD;
        },
        effectiveWidth() {
            return this.isUserCollapsed ? COLLAPSED_WIDTH : this.sidebarWidth;
        },
        isDesktopViewport() {
            return this.viewportWidth >= DESKTOP_BREAKPOINT
        },
        sidebarStyle() {
            const w = this.effectiveWidth;
            const style = {
                width: w + 'px',
                minWidth: w + 'px',
                maxWidth: w + 'px'
            };

            if (this.isDesktopViewport) {
                style.position = 'fixed';
                style.top = '0';
                style.left = '0';
                style.bottom = '0';
                style.height = '100vh';
                style.maxHeight = '100vh';
                style.transform = 'translateX(0)';
                style.overflow = 'visible';
                style.borderRadius = '0';
                style.zIndex = '1030';
                style.pointerEvents = 'auto';
            }

            return style;
        }
    },
    mounted() {
        this.loadSavedWidth();
        this.applyBodyPadding();
        window.addEventListener('resize', this.onWindowResize);

        this._onSidebarClick = (e) => {
            const link = e.target.closest('a, router-link, [to]');
            if (!link) return;

            // Check if it is an accordion dropdown toggle (only expands sub-menu, shouldn't close sidebar)
            const isAccordionToggle = link.hasAttribute('aria-expanded') ||
                                      link.classList.contains('nk-menu-toggle') ||
                                      link.getAttribute('data-toggle') === 'collapse' ||
                                      (link.getAttribute('href') === '#' && !link.hasAttribute('to')) ||
                                      link.classList.contains('sip-side-profile__btn') ||
                                      link.closest('.nk-menu-trigger');

            if (link.id === 'HideRightSideBarMenu') {
                this.closeMobileSidebar();
                return;
            }

            if (isAccordionToggle) {
                return;
            }

            // Real navigation item clicked: auto-dismiss mobile sidebar immediately
            this.closeMobileSidebar();
        };
        if (this.$el) {
            this.$el.addEventListener('click', this._onSidebarClick);
        }
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onWindowResize);
        if (this.$el && this._onSidebarClick) {
            this.$el.removeEventListener('click', this._onSidebarClick);
        }
        this.stopResize();
    },
    methods: {
        closeMobileSidebar() {
            const sidebar = document.getElementById('MainSideBarRightMenu');
            if (sidebar && sidebar.classList.contains('nk-sidebar-active')) {
                sidebar.classList.remove('nk-sidebar-active');
            }
            document.body.classList.remove('nav-shown', 'sip-mobile-sidebar-open');
            window.dispatchEvent(new CustomEvent('sip-sidebar-closed'));
        },
        loadSavedWidth() {
            try {
                const saved = localStorage.getItem(STORAGE_KEY);
                if (saved !== null) {
                    const w = parseInt(saved, 10);
                    if (!isNaN(w) && w >= MIN_WIDTH && w <= MAX_WIDTH) this.sidebarWidth = w;
                }
            } catch (e) {}
        },
        saveWidth() {
            try {
                localStorage.setItem(STORAGE_KEY, String(this.sidebarWidth));
            } catch (e) {}
        },
        applyBodyPadding() {
            const w = this.isDesktopViewport ? this.effectiveWidth : 0;
            document.body.style.setProperty('--sidebar-width', w + 'px');
            document.body.style.setProperty('--sidebar-inset', '0px');
            document.body.style.setProperty('--sidebar-body-offset', '0px');
        },
        onWindowResize() {
            this.viewportWidth = window.innerWidth;
            if (!this.isResizing) this.$nextTick(() => this.applyBodyPadding());
        },
        startResize(e) {
            if (this.isResizing) return;
            this.isResizing = true;
            const startX = e.clientX;
            const startW = this.sidebarWidth;
            const move = (e) => {
                const delta = e.clientX - startX;
                let next = startW + delta;
                next = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, next));
                this.sidebarWidth = next;
                this.applyBodyPadding();
            };
            const up = () => {
                this.isResizing = false;
                this.saveWidth();
                document.removeEventListener('mousemove', move);
                document.removeEventListener('mouseup', up);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            document.addEventListener('mousemove', move);
            document.addEventListener('mouseup', up);
        },
        stopResize() {
            this.isResizing = false;
        },
        logout() {
            helper.logout().then(() => {
                this.$router.push('/login');
            })
        },
        getAuthUser(name) {
            return helper.getAuthUser(name);
        },
        hasPermission(permission) {
            return helper.hasPermission(permission);
        },
        getConfig(config) {
            return helper.getConfig(config);
        }
    },
    watch: {
        $route() {
            this.closeMobileSidebar();
        },
        effectiveWidth: {
            handler() {
                this.applyBodyPadding();
            }
        },
        getLogoUrl() {
            this.logoLoadError = false;
        }
    }
}
</script>

<style scoped>
.nk-sidebar-menu {
    overflow: auto;
    overflow-anchor: none;
}
</style>
